import { Variants } from 'framer-motion';

// Easing curves - cast as const for proper Framer Motion typing
export const easing = {
  smooth: [0.4, 0, 0.2, 1] as const,
  bounce: [0.68, -0.55, 0.265, 1.55] as const,
  snappy: [0.25, 0.1, 0.25, 1] as const,
};

// Page/Container animations
export const pageVariants: Variants = {
  initial: { opacity: 0, y: 20 },
  animate: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.4,
      ease: easing.smooth,
    }
  },
  exit: {
    opacity: 0,
    y: -10,
    transition: { duration: 0.2 }
  },
};

// Stagger container for lists.
//
// NOTE: variant propagation from this container down to `staggerItem` children
// does not fire under framer-motion 12 — the children never leave `initial`
// and the page renders fully invisible. Call sites therefore pass
// `initial="initial" animate="animate"` on each item explicitly rather than
// relying on inheritance.
export const staggerContainer: Variants = {
  initial: {},
  animate: {
    transition: {
      staggerChildren: 0.05,
      delayChildren: 0.1,
    },
  },
};

// Stagger item
export const staggerItem: Variants = {
  initial: { opacity: 0, y: 20 },
  animate: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.3,
      ease: easing.smooth,
    }
  },
};

// Fade in animation
export const fadeIn: Variants = {
  initial: { opacity: 0 },
  animate: {
    opacity: 1,
    transition: { duration: 0.3 }
  },
  exit: {
    opacity: 0,
    transition: { duration: 0.2 }
  },
};

// Fade in up animation
export const fadeInUp: Variants = {
  initial: { opacity: 0, y: 20 },
  animate: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.4,
      ease: easing.smooth,
    }
  },
  exit: {
    opacity: 0,
    y: 10,
    transition: { duration: 0.2 }
  },
};

// Scale fade animation (for modals)
export const scaleIn: Variants = {
  initial: { opacity: 0, scale: 0.95 },
  animate: {
    opacity: 1,
    scale: 1,
    transition: {
      duration: 0.2,
      ease: easing.snappy,
    }
  },
  exit: {
    opacity: 0,
    scale: 0.95,
    transition: { duration: 0.15 }
  },
};

// Backdrop animation
export const backdropVariants: Variants = {
  initial: { opacity: 0 },
  animate: {
    opacity: 1,
    transition: { duration: 0.2 }
  },
  exit: {
    opacity: 0,
    transition: { duration: 0.15 }
  },
};

// Dropdown animation
export const dropdownVariants: Variants = {
  initial: { opacity: 0, y: -10, scale: 0.95 },
  animate: {
    opacity: 1,
    y: 0,
    scale: 1,
    transition: {
      duration: 0.15,
      ease: easing.snappy,
    }
  },
  exit: {
    opacity: 0,
    y: -10,
    scale: 0.95,
    transition: { duration: 0.1 }
  },
};

// Slide down animation
export const slideDown: Variants = {
  initial: { opacity: 0, height: 0 },
  animate: {
    opacity: 1,
    height: 'auto',
    transition: {
      duration: 0.3,
      ease: easing.smooth,
    }
  },
  exit: {
    opacity: 0,
    height: 0,
    transition: { duration: 0.2 }
  },
};

// Card hover animation
export const cardHover = {
  rest: {
    scale: 1,
    boxShadow: '0 1px 3px 0 rgb(0 0 0 / 0.1)',
    transition: { duration: 0.2, ease: easing.smooth }
  },
  hover: {
    scale: 1.02,
    boxShadow: '0 10px 15px -3px rgb(0 0 0 / 0.1), 0 4px 6px -4px rgb(0 0 0 / 0.1)',
    transition: { duration: 0.2, ease: easing.smooth }
  },
};

// Subtle card hover (less pronounced)
export const subtleCardHover = {
  rest: {
    y: 0,
    transition: { duration: 0.2, ease: easing.smooth }
  },
  hover: {
    y: -2,
    transition: { duration: 0.2, ease: easing.smooth }
  },
};

// Toast animations
export const toastVariants: Variants = {
  initial: { opacity: 0, y: 50, scale: 0.9 },
  animate: {
    opacity: 1,
    y: 0,
    scale: 1,
    transition: {
      duration: 0.3,
      ease: easing.bounce,
    }
  },
  exit: {
    opacity: 0,
    y: 20,
    scale: 0.9,
    transition: { duration: 0.2 }
  },
};

// Expand/collapse animation
export const expandVariants: Variants = {
  initial: { opacity: 0, height: 0 },
  animate: {
    opacity: 1,
    height: 'auto',
    transition: {
      height: { duration: 0.3, ease: easing.smooth },
      opacity: { duration: 0.2, delay: 0.1 },
    }
  },
  exit: {
    opacity: 0,
    height: 0,
    transition: {
      height: { duration: 0.2, ease: easing.smooth },
      opacity: { duration: 0.1 },
    }
  },
};

// Tab indicator animation
export const tabIndicator = {
  layoutId: 'activeTab',
  transition: { type: 'spring', stiffness: 500, damping: 30 },
};

// Skeleton pulse animation (CSS keyframes reference)
export const skeletonPulse = {
  animate: {
    opacity: [0.5, 1, 0.5],
    transition: {
      duration: 1.5,
      repeat: Infinity,
      ease: 'easeInOut',
    },
  },
};

// List item animation for expandable rows
export const listItemExpand: Variants = {
  initial: { opacity: 0, height: 0 },
  animate: {
    opacity: 1,
    height: 'auto',
    transition: {
      duration: 0.3,
      ease: easing.smooth,
    }
  },
  exit: {
    opacity: 0,
    height: 0,
    transition: { duration: 0.2 }
  },
};

// Badge/status change animation
export const statusChange: Variants = {
  initial: { scale: 0.8, opacity: 0 },
  animate: {
    scale: 1,
    opacity: 1,
    transition: {
      type: 'spring',
      stiffness: 500,
      damping: 25,
    }
  },
  exit: {
    scale: 0.8,
    opacity: 0,
    transition: { duration: 0.1 }
  },
};
